import { useEffect } from 'react'
import { getPlatformConfig } from '../lib/supabase'

// Componente sin UI que aplica dinamicamente el branding de la plataforma:
// - <link rel="icon"> (favicon en el tab del browser)
// - <link rel="apple-touch-icon"> (icono al guardar en iOS home screen)
// - <link rel="manifest"> con manifest dinamico via Blob URL (icono PWA install)
// - document.title (nombre de la plataforma)
// - theme-color meta tag
//
// Limitaciones del PWA install icon:
// - Browsers ya instalados como PWA NO actualizan el icono hasta desinstalar/reinstalar.
// - Safari iOS tiene soporte limitado para manifest dinamico; usa apple-touch-icon como fallback.
// - El icono ideal es PNG 512x512 con padding interno >=20% para zona segura "maskable".
const DynamicPlatformBranding = () => {
  useEffect(() => {
    let blobUrl = null
    let cancelled = false

    const apply = async () => {
      const { data } = await getPlatformConfig()
      if (cancelled || !data) return

      // Favicon de la PESTAÑA del navegador: sobre fondo claro, el icono claro
      // (MK oscura) se ve bien.
      const iconUrl = data.favicon_url || data.login_logo_url || null
      // Icono de la APP (home screen) y del SPLASH nativo: sobre fondo oscuro,
      // necesita la version oscura (MK blanca, maskable con padding). NO se usa
      // el favicon claro aqui, o la MK oscura se pierde en el negro (bug del
      // splash nativo). Default al asset del repo; se puede sobreescribir con
      // platform_config.app_icon_url si algun dia se quiere un icono propio.
      const pub = process.env.PUBLIC_URL || ''
      const appIcon192 = data.app_icon_url || `${pub}/favicon/web-app-manifest-192x192.png`
      const appIcon512 = data.app_icon_url || `${pub}/favicon/web-app-manifest-512x512.png`
      const appIconUrl = appIcon512
      const name = (data.platform_name || 'MYKLUBB').trim()
      const themeBg = data.login_color_bg || '#0D0D0D'

      // 1. Pestaña del navegador vs icono de app: superficies distintas.
      if (iconUrl) upsertLink('icon', iconUrl)
      upsertLink('apple-touch-icon', appIconUrl)

      // 2. document.title
      if (name && !document.title.startsWith(name)) {
        document.title = name
      }

      // 3. meta theme-color
      let themeMeta = document.querySelector('meta[name="theme-color"]')
      if (!themeMeta) {
        themeMeta = document.createElement('meta')
        themeMeta.name = 'theme-color'
        document.head.appendChild(themeMeta)
      }
      themeMeta.content = themeBg

      // 4. manifest dinamico. Icono de app/splash = version oscura (appIcon*),
      // NO el favicon claro. Se genera siempre (el icono de app tiene default),
      // no solo cuando hay favicon configurado.
      {
        const manifest = {
          name,
          short_name: name,
          description: `${name} - Plataforma de gestion deportiva.`,
          icons: [
            { src: appIcon192, sizes: '192x192', type: 'image/png', purpose: 'any' },
            { src: appIcon192, sizes: '192x192', type: 'image/png', purpose: 'maskable' },
            { src: appIcon512, sizes: '512x512', type: 'image/png', purpose: 'any' },
            { src: appIcon512, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
          ],
          start_url: '/',
          display: 'standalone',
          orientation: 'portrait-primary',
          theme_color: themeBg,
          background_color: themeBg,
          categories: ['productivity', 'sports'],
          lang: 'es',
          scope: '/',
          id: 'myklubb',
        }

        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
        blobUrl = URL.createObjectURL(blob)

        let manifestLink = document.querySelector('link[rel="manifest"]')
        if (!manifestLink) {
          manifestLink = document.createElement('link')
          manifestLink.rel = 'manifest'
          document.head.appendChild(manifestLink)
        }
        manifestLink.href = blobUrl
      }
    }

    apply()

    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [])

  return null
}

const upsertLink = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
}

export default DynamicPlatformBranding
